/**
 * Surface copy for the admin eval screens. The value sets themselves are
 * contract enums (`contracts/enums/evals`); only the wording is frontend-only,
 * chosen for the operator reading a batch progress table rather than for any
 * artifact the backend renders.
 *
 * Every record is keyed by its union so a new enum member fails to compile
 * until it is named here. Nothing derives a label by transforming the stored
 * value: `pending_capture` reads as "Fetching page", which no title-caser
 * could know.
 */

import type {
  EvalBatchStatus,
  EvalComparisonStatus,
  EvalJudgeKind,
  EvalVariantSlot,
} from "contracts/enums/evals";

export const EVAL_COMPARISON_STATUS_COPY: Record<EvalComparisonStatus, string> =
  {
    pending_capture: "Fetching page",
    awaiting_completion: "Both variants running",
    judge_requested: "Waiting on the judge",
    judged: "Judged",
    failed: "Failed",
  };

/**
 * The badge modifier for a comparison status, reusing the shared `st-*` status
 * classes the run tables already style.
 */
export const EVAL_COMPARISON_STATUS_TONE: Record<EvalComparisonStatus, string> =
  {
    pending_capture: "st-running",
    awaiting_completion: "st-running",
    judge_requested: "st-running",
    judged: "st-done",
    failed: "st-failed",
  };

export const EVAL_BATCH_STATUS_COPY: Record<EvalBatchStatus, string> = {
  running: "Running",
  completed: "Completed",
  failed: "Finished with failures",
};

export const EVAL_BATCH_STATUS_TONE: Record<EvalBatchStatus, string> = {
  running: "st-running",
  completed: "st-done",
  failed: "st-failed",
};

/**
 * A winner is named by its variant's own label ("control", "deepseek
 * candidate") — see `winnerLabel`, which is exhaustive over the winner enum.
 * Only the tie has wording of its own, because it names no variant.
 */
export const EVAL_TIE_COPY = "Tie";

export const EVAL_VARIANT_SLOT_COPY: Record<EvalVariantSlot, string> = {
  a: "Variant A",
  b: "Variant B",
};

export const EVAL_JUDGE_KIND_COPY: Record<EvalJudgeKind, string> = {
  llm: "Model verdict",
  human: "Human verdict",
};

/**
 * How the pair's evidence compares, as the operator needs to read it. The
 * contract carries `evidenceMatches` as `boolean | null`; naming the three
 * cases here keeps the screens from re-deciding what a null means each time.
 */
export const EVIDENCE_STATES = [
  "identical",
  "divergent",
  "incomplete",
] as const;
export type EvidenceState = (typeof EVIDENCE_STATES)[number];
export const EVIDENCE_STATE = {
  IDENTICAL: "identical",
  DIVERGENT: "divergent",
  INCOMPLETE: "incomplete",
} as const satisfies Record<string, EvidenceState>;

export const EVIDENCE_STATE_COPY: Record<EvidenceState, string> = {
  identical: "Same evidence",
  divergent: "Different evidence — not a fair comparison",
  incomplete: "Evidence not captured yet",
};

export const EVIDENCE_STATE_TONE: Record<EvidenceState, string> = {
  identical: "st-done",
  divergent: "st-failed",
  incomplete: "st-running",
};

/**
 * Whether an analyst may record a verdict on this comparison right now, and
 * why not when they may not. Derived from the comparison's own status and
 * judgments — never from the wording of a rejected write.
 */
export const HUMAN_VERDICT_STATES = ["open", "recorded", "too_early"] as const;
export type HumanVerdictState = (typeof HUMAN_VERDICT_STATES)[number];
export const HUMAN_VERDICT_STATE = {
  OPEN: "open",
  RECORDED: "recorded",
  TOO_EARLY: "too_early",
} as const satisfies Record<string, HumanVerdictState>;

export const HUMAN_VERDICT_STATE_COPY: Record<HumanVerdictState, string> = {
  open: "Record your verdict — it overrides the model's.",
  recorded: "Your verdict is recorded and overrides the model's.",
  too_early: "Both variants must finish before a verdict can be recorded.",
};
